import { Subject } from 'rxjs';

import { Customer, Dashlet, RealtimeGatewayDocument, Threshold } from '@models/index';

export class TrunkGroup {
    name!: string;
    number!: string;
    size!: number;
    active!: number;
    severity!: number;
    commandType!: string;
    thresholds: Threshold[] = [];
    type!: string;
}

export class DashletTrunkGroupList extends Dashlet {
    readonly commandTypeIdOutOfServiceThreshold = 'B0998099-FA3E-4DA0-BC22-162485E1C058';

    public readonly commandTypeIdIpoUtilization = '78ab641f-fbdd-497e-bd93-e2ecabb51831';
    public readonly commandTypeIdAcmUtilization = 'f91b4082-1e63-43d4-820f-21b316afd1f2';
    public readonly commandTypeIdAcmTrunkGroups = 'a8de1686-7f14-4391-ba45-26dfd5fb0370';
    public readonly commandTypeIdAepUtilization = 'C31BC130-BA99-4ECD-86F5-C93B8E255D5E';
    public equipmentId!: string;
    private trunkGroups!: string[];
    public customer!: Customer;

    private ipoTrunks!: TrunkGroup[];
    private acmTrunks!: TrunkGroup[];
    trunks: TrunkGroup[] = [];

    public thresholds: Threshold[] = [];

    public loading: boolean = true;

    public trunksReceived: Subject<null> = new Subject();
    public settingsChanged: Subject<boolean> = new Subject<boolean>();
    public sizeChange = new Subject<number>();

    constructor() {
        super();

        this.sizes = [
            {
                id: 0,
                label: 'Small',
                cols: 5,
                rows: 3
            },
            {
                id: 1,
                label: 'Medium',
                cols: 5,
                rows: 6
            },
            {
                id: 2,
                label: 'Large',
                cols: 5,
                rows: 8
            }
        ];
        this.applySize(0);
    }

    public applySize(id: number): void {
        super.applySize(id);
        this.sizeChange.next(id);
    }

    applySettings(v: { [key: string]: any }) {
        super.applySettings(v);
        const trunkGroups = v.trunkGroups || [];
        this.configured = v.customer && v.location && v.equipment && trunkGroups.length > 0;
        if (v.customer) {
            this.customer = new Customer(v.customer.value, v.customer.label);
        } else {
            this.customer = new Customer('', '');
        }
        this.equipmentId = v.equipment && v.equipment.value ? v.equipment.value : null;
        this.generatedNameTag = this.configured ? `${v.location.label} | ${v.equipment.label}` : 'Unconfigured';
        this.customNameTag = v.nameTag;

        this.trunkGroups = trunkGroups.map((i: any) => i.value?.toString());

        this.trunksReceived.next(null);
        if (this.configured) {
            this.settingsChanged.next(true);
        }
    }

    dispose() {}

    resetData() {
        this.ipoTrunks = [];
        this.acmTrunks = [];
        this.trunks = [];
    }

    public processThresholdData(data: Threshold[]): any {
        if (data && data.length > 0) {
            data.forEach((data: any) => {
                const threshold = new Threshold();
                threshold.commandTypeId = data['commandTypeId'];
                threshold.dapThresholdTemplateId = data['dapThresholdTemplateId'];
                threshold.alarmName = data['alarmName'];
                threshold.name = data['name'];
                threshold.customerId = data['customerId'];
                threshold.dAPMessageFullClassName = data['dapmessageFullClassName'];
                threshold.dapThresholdId = data['dapThresholdId'];
                threshold.description = data['description'];
                threshold.equipmentId = data['equipmentId'];
                threshold.jSONRules = data['jsonrules'];
                threshold.locationId = data['locationId'];
                const jsonObj = JSON.parse(data['jsonrules']);
                jsonObj.forEach((element: any) => {
                    if (element['field'] === 'groupNumber' || element['field'] === 'trunkNumber') {
                        threshold.fieldNumber = element['value']?.toString();
                    } else if (element['field'] === 'grpNumber') {
                        threshold.fieldNumber = element['value']?.toString();
                        threshold.field = threshold.fieldNumber;
                    }
                });
                this.thresholds.push(threshold);
            });
            this.processThresholdsMapping();
        }
    }

    public processDataIpo(utilizationData: RealtimeGatewayDocument[]) {
        if (utilizationData !== null) {
            // get utilization for each trunk
            this.acmTrunks = [];
            for (const table of utilizationData) {
                for (const row of table.data) {
                    const t = new TrunkGroup();
                    t.number = (+row.TrunkNumber).toString();
                    t.name = 'Group ' + t.number;
                    t.size = +row.NumberOfChannels;
                    t.active = +row.ChannelsInUse;
                    t.commandType = this.commandTypeIdIpoUtilization;
                    t.type = 'ipo';

                    if (this.trunkGroups.some(num => t.number === num)) {
                        this.acmTrunks.push(t);
                    }
                }
            }
            this.processTrunks();
        }
    }

    public processDataAep(utilizationData: RealtimeGatewayDocument[]) {
        if (utilizationData !== null) {
            this.acmTrunks = [];
            for (const table of utilizationData) {
                const trunkNames = table.data
                    .map((row: any) => row.Trunk)
                    .filter((name: any, i: number, a: any[]) => i === a.indexOf(name));

                trunkNames.forEach((trunkName: string) => {
                    const channels = table.data.filter(row => row.Trunk === trunkName);

                    let connected = 0;
                    for (const channel of channels) {
                        if (channel.CallState?.toLowerCase() === 'connected') {
                            connected++;
                        }
                    }

                    const t = new TrunkGroup();
                    t.name = trunkName;
                    t.size = channels.length;
                    t.active = connected;
                    t.commandType = this.commandTypeIdAepUtilization;
                    t.type = 'aep';

                    if (this.trunkGroups.some(name => t.name === name)) {
                        this.acmTrunks.push(t);
                    }
                });
            }
            this.processTrunks();
        }
    }

    public processDataAcm(groupsData: any[], utilizationData: any[]) {
        if (groupsData?.length && utilizationData?.length) {
            // create names map
            const nameMap: { [trunkGroupNumber: string]: string } = {};
            for (const table of groupsData) {
                for (const row of table.data) {
                    nameMap[row.GroupNumber.toString()] = row.GroupName;
                }
            }
            // get utilization for each trunk
            this.acmTrunks = [];
            for (const table of utilizationData) {
                for (const row of table.data) {
                    const t = new TrunkGroup();
                    t.number = row.GroupNumber.toString();
                    t.name = nameMap[t.number] || 'Unknown';
                    t.size = +row.GroupSize;

                    t.active = +row.ActiveMembers;
                    t.commandType = this.commandTypeIdAcmUtilization;
                    t.type = 'acm';

                    if (this.trunkGroups.some(num => t.number === num)) {
                        this.acmTrunks.push(t);
                    }
                }
            }
            this.processTrunks();
        }
    }

    private processTrunks() {
        this.loading = false;
        const trunks = (this.acmTrunks || []).concat(this.ipoTrunks || []);

        // colorise
        for (const t of trunks) {
            const delta = t.size - t.active;
            let severity: number;
            if (t.size <= 0) {
                severity = -1;
            } else if (delta < t.size * 0.1) {
                severity = 2;
            } else if (delta < t.size * 0.25) {
                severity = 1;
            } else {
                severity = 0;
            }
            t.severity = severity;
        }
        this.trunks = trunks;

        this.trunksReceived.next(null);

        this.processThresholdsMapping();
    }

    private processThresholdsMapping() {
        this.thresholds.forEach(threshold => {
            const trunk = this.trunks.find(
                trunk =>
                    trunk.number === threshold.fieldNumber &&
                    (threshold.commandTypeId === this.commandTypeIdAcmUtilization.toLowerCase() ||
                        threshold.commandTypeId === this.commandTypeIdAepUtilization.toLowerCase() ||
                        threshold.commandTypeId === this.commandTypeIdIpoUtilization.toLowerCase() ||
                        threshold.commandTypeId === this.commandTypeIdOutOfServiceThreshold.toLowerCase())
            );
            if (trunk) {
                trunk.thresholds.push(threshold);
            }
        });
    }
}
